import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Signal,
  TemplateRef,
  afterRenderEffect,
  computed,
  contentChild,
  inject,
  input,
  signal
} from "@angular/core";
import { Observable, lastValueFrom } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AbilityAction,
  AbilitySubject,
  UserService
} from "src/app/services/user.service";
import { some } from "lodash";
import { SseEvents } from "src/app/sse/sse-events";
import { BrokerService } from "src/app/sse/broker.service";
import { ContainerNoDataContentDirective } from "src/app/components/ui/container/directives/container-no-data-content.directive";
import { ContainerContentDirective } from "src/app/components/ui/container/directives/container-content.directive";
import { TabServiceTab } from "src/app/services/tab.service";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("ContainerBaseComponent");
export type ContainerLoadFn<T> = (loadParams: any | undefined) => Observable<T>;

export type ContainerNoDataFn<T> = (data: any) => boolean;

@Component({
  standalone: false,
  selector: "pms-container",
  templateUrl: "container-base.component.html",
  styles: `
    :host {
      display: flex;
      flex: 1;
    }
  `
})
export class ContainerBaseComponent implements OnInit {
  loaderType = input<"spinner" | "skeleton-page-detail">("spinner");

  @Input()
  able: Array<[AbilityAction, AbilitySubject, any?]> | undefined;

  @Input()
  load: ContainerLoadFn<any> | undefined;

  @Input()
  loadParams: any | undefined;

  @Input()
  noData: ContainerNoDataFn<any> | null = null;

  @Input()
  loaderCssClass: string | undefined;

  tab = input<TabServiceTab | undefined>(undefined);

  hasPendingUpdates = signal<boolean>(false);

  brokerEvents = input<SseEvents[] | undefined>(undefined);

  @Output()
  data: EventEmitter<any> = new EventEmitter<any>();

  template = input<TemplateRef<unknown> | undefined>(undefined);
  contentTemplateRef = contentChild(ContainerContentDirective);

  computedTemplateRef: Signal<TemplateRef<unknown> | undefined> = computed(
    () => {
      const contentTemplateRef: ContainerContentDirective | undefined =
        this.contentTemplateRef();
      const templateRef: TemplateRef<unknown> | undefined = this.template();

      return templateRef || contentTemplateRef?.templateRef;
    }
  );

  @ContentChild(ContainerNoDataContentDirective)
  noDataTemplateRef!: ContainerNoDataContentDirective;

  loading = signal<boolean>(false);
  error = signal<boolean>(false);
  unable = signal<boolean>(false);
  unableHttp = signal<boolean>(false);
  unableAbility = signal<boolean>(false);

  lastData: any | null = null;

  #brokersListenersIds: Array<{ id: string; type: SseEvents }> = [];

  readonly #userService = inject(UserService);
  readonly #brokerService = inject(BrokerService);

  constructor() {
    afterRenderEffect(() => {
      const brokerEvents = this.brokerEvents();
      const tab = this.tab();

      if (!brokerEvents || !tab) {
        return;
      }

      this.#brokersListenersIds.forEach(({ id, type }) => {
        this.#brokerService.removeListener({ id, type });
      });

      brokerEvents.forEach((brokerEvent: SseEvents) => {
        const id = this.#brokerService.addListener({
          type: brokerEvent,
          listener: () => {
            this.hasPendingUpdates.set(true);
          },
          tab
        });

        this.#brokersListenersIds.push({ id, type: brokerEvent });
      });
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  async refresh(): Promise<void> {
    await this.loadData();
  }

  async refreshSilent(): Promise<void> {
    await this.loadData(true);
  }

  startLoading(): void {
    this.loading.set(true);
    this.error.set(false);
  }

  private async loadData(silent?: boolean): Promise<void> {
    this.error.set(false);

    this.unableHttp.set(false);
    this.unableAbility.set(false);

    if (this.able) {
      const unable = some(
        this.able.map(([action, subject, condition]) => {
          return !this.#userService.able(
            action,
            subject,
            condition
              ? Object.keys(condition).length > 0
                ? condition
                : undefined
              : undefined
          );
        })
      );

      this.unable.set(unable);
      this.unableAbility.set(true);

      this.error.set(unable);

      if (unable) {
        return;
      }
    }

    if (this.load) {
      if (!silent) {
        this.loading.set(true);
      }

      try {
        const result = await lastValueFrom(this.load(this.loadParams));
        this.lastData = result;
        this.data.emit(result);
      } catch (e) {
        logger.error(e);

        this.error.set(true);

        switch (true) {
          case e instanceof HttpErrorResponse: {
            if ((<HttpErrorResponse>e).status === 403) {
              this.unable.set(true);
              this.unableHttp.set(true);
            } else {
              this.unable.set(false);
            }
            break;
          }
        }
      } finally {
        this.loading.set(false);
      }
    }
  }
}
